import AsyncStorage from '@react-native-async-storage/async-storage';
import { InputStroke } from '@eduwrite/shared';

const PENDING_KEY = 'eduwrite_pending_submissions';
const PENDING_COMPLETIONS_KEY = 'eduwrite_pending_task_completions';

export interface PendingSubmission {
    id?: string;
    activityId: string;
    studentId: string;
    classId: string;
    targetText?: string;
    activityType?: string;
    activityConfig?: unknown;
    strokes: InputStroke[];
    timestamp: number;
}

export interface PendingTaskCompletion {
    id?: string;
    studentId: string;
    assignmentId: string;
    serverUrl: string;
    score?: number;
    timestamp: number;
}

export class OfflineFirstStorage {
    /**
     * Saves a new pending submission to offline storage
     */
    static async savePendingSubmission(sub: PendingSubmission): Promise<void> {
        try {
            const pending = await this.getPendingSubmissions();
            pending.push({ ...sub, id: `offline-${Date.now()}` });
            await AsyncStorage.setItem(PENDING_KEY, JSON.stringify(pending));
        } catch (e) {
            console.error('[OfflineStorage] Failed to save submission:', e);
        }
    }

    /**
     * Retrieves all currently pending submissions
     */
    static async getPendingSubmissions(): Promise<PendingSubmission[]> {
        try {
            const raw = await AsyncStorage.getItem(PENDING_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            console.error('[OfflineStorage] Failed to read submissions:', e);
            return [];
        }
    }

    /**
     * Clears the pending queue (typically called after successful sync)
     */
    static async clearPending(): Promise<void> {
        try {
            await AsyncStorage.removeItem(PENDING_KEY);
        } catch (e) {
            console.error('[OfflineStorage] Failed to clear submissions:', e);
        }
    }

    /**
     * Saves a pending task completion when offline or when network requests fail
     */
    static async savePendingTaskCompletion(comp: PendingTaskCompletion): Promise<void> {
        try {
            const pending = await this.getPendingTaskCompletions();
            pending.push({ ...comp, id: comp.id || `comp-${Date.now()}` });
            await AsyncStorage.setItem(PENDING_COMPLETIONS_KEY, JSON.stringify(pending));
        } catch (e) {
            console.error('[OfflineStorage] Failed to save pending completion:', e);
        }
    }

    /**
     * Retrieves all currently pending task completions
     */
    static async getPendingTaskCompletions(): Promise<PendingTaskCompletion[]> {
        try {
            const raw = await AsyncStorage.getItem(PENDING_COMPLETIONS_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch (e) {
            console.error('[OfflineStorage] Failed to read pending completions:', e);
            return [];
        }
    }

    /**
     * Clears pending task completions after successful sync
     */
    static async clearPendingTaskCompletions(): Promise<void> {
        try {
            await AsyncStorage.removeItem(PENDING_COMPLETIONS_KEY);
        } catch (e) {
            console.error('[OfflineStorage] Failed to clear pending completions:', e);
        }
    }
}
